// AuthContext.js - Authentication Context Provider

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  getAccessToken,
  setAccessToken,
  clearSession,
  getStoredUser,
  persistSession,
  hasRefreshCapability,
  setRefreshToken,
  getRefreshToken,
} from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Restore session on app load
  const restoreSession = useCallback(async () => {
    if (!hasRefreshCapability()) {
      setIsLoading(false);
      return;
    }

    try {
      const refreshToken = getRefreshToken();
      const response = await axios.post('/api/auth/refresh', { refreshToken });

      const { accessToken, refreshToken: newRefreshToken, user: userData } = response.data;

      setAccessToken(accessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }
      persistSession(userData);
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.log('Session restoration failed:', error.message);
      clearSession();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // Login function
  const login = useCallback((accessToken, refreshToken, userData, extra = {}) => {
    setAccessToken(accessToken);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    persistSession(userData, extra);
    setUser({ ...userData, ...extra });
    setIsAuthenticated(true);
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const token = getAccessToken();
      const refreshToken = getRefreshToken();
      if (token) {
        await axios.post('/api/auth/logout', { refreshToken }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch {
      // Ignore errors - we're logging out anyway
    }
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Update user data (e.g., after profile changes)
  const updateUser = useCallback((userData, extra = {}) => {
    persistSession(userData, extra);
    setUser({ ...userData, ...extra });
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    restoreSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
